/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  getHeaders,
  handleResponse,
  throwResponseError,
} from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type { MaritimeWarningsConfig } from '../../components/MaritimeWarningsForm/types';
import type {
  DrawingFromBE,
  DrawingFromFE,
  DrawingListItem,
  FetchDrawingParams,
} from '../../store/warningsDrawings/types';
import type {
  MaritimeWarning,
  MaritimeWarningsType,
  PublicWarning,
  WarningEvent,
  WarningEventsConfig,
} from '../../store/warningsForm/types';

const config = sessionStorageProvider.getConfig();

const publicWarningBaseUrl = config.GW_DRAWINGS_BASE_URL || '';

// API
export const getPublicWarningList = async (
  auth: Credentials,
  apiParams: Record<string, string> = {},
): Promise<PublicWarning[]> => {
  const params = new URLSearchParams(apiParams);
  const url = `${publicWarningBaseUrl}/warnings${params.toString() ? `?${params}` : ''}`;
  return fetch(url, {
    headers: getHeaders(auth),
  })
    .then(handleResponse)
    .then((warnings: unknown) =>
      (warnings as Partial<PublicWarning>[]).map(
        (warning: Partial<PublicWarning>) => ({
          ...warning,
          type: warning.warningDetail?.domain || 'public',
        }),
      ),
    ) as Promise<PublicWarning[]>;
};

export const getPublicWarningById = async (
  warningId: string,
  auth: Credentials,
): Promise<PublicWarning> =>
  fetch(`${publicWarningBaseUrl}/warnings/${warningId}`, {
    headers: getHeaders(auth),
  })
    .then(handleResponse)
    .then((warning: unknown) => {
      const publicWarning = warning as Partial<PublicWarning>;
      return {
        ...publicWarning,
        type: publicWarning.warningDetail?.domain || 'public',
      };
    }) as Promise<PublicWarning>;

export const updatePublicWarningEditor = async (
  warningId: string,
  isEditor: boolean,
  auth: Credentials,
  force?: boolean,
): Promise<Headers | null> =>
  fetch(
    `${publicWarningBaseUrl}/warnings/${warningId}?editor=${isEditor ? 'true' : 'false'}&force=${force ? 'true' : 'false'}`,
    {
      headers: getHeaders(auth),
      method: 'POST',
    },
  ).then(handleResponse) as Promise<Headers | null>;

export const updatePublicWarning = async (
  warningId: string,
  auth: Credentials,
  data: PublicWarning,
): Promise<Headers | null> =>
  fetch(`${publicWarningBaseUrl}/warnings/${warningId}`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers | null>;

export const postPublicWarning = async (
  auth: Credentials,
  data: PublicWarning,
): Promise<Headers | null> =>
  fetch(`${publicWarningBaseUrl}/warnings/`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers | null>;

export const postMaritimeWarnings = async (
  auth: Credentials,
  data: MaritimeWarning[],
): Promise<Headers | null> =>
  fetch(`${publicWarningBaseUrl}/maritime/`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers | null>;

export const updateMaritimeWarningsEditor = async (
  domain: MaritimeWarningsType,
  isEditor: boolean,
  auth: Credentials,
  force?: boolean,
): Promise<Headers | null> =>
  fetch(
    `${publicWarningBaseUrl}/maritime/${domain}?editor=${isEditor ? 'true' : 'false'}&force=${force ? 'true' : 'false'}`,
    {
      headers: getHeaders(auth),
      method: 'POST',
    },
  ).then(handleResponse) as Promise<Headers | null>;

export const deleteWarning = async (
  warningId: string,
  auth: Credentials,
): Promise<null> =>
  fetch(`${publicWarningBaseUrl}/warnings/${warningId}?delete=true`, {
    headers: getHeaders(auth),
    method: 'POST',
  }).then(handleResponse) as Promise<null>;

export const getDrawings = async (
  auth: Credentials,
  params?: FetchDrawingParams,
): Promise<DrawingListItem[]> => {
  const query = params
    ? new URLSearchParams(
        Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      ).toString()
    : '';

  const url = `${publicWarningBaseUrl}/drawings${query ? `?${query}` : ''}`;

  return fetch(url, {
    headers: getHeaders(auth),
  })
    .then(handleResponse)
    .then((drawings) => drawings as DrawingListItem[]);
};

export const getDrawingsPredefinedAreas = async (
  auth: Credentials,
): Promise<DrawingListItem[]> => {
  const url = `${publicWarningBaseUrl}/drawings/predefinedAreas`;

  return fetch(url, {
    headers: getHeaders(auth),
  })
    .then(handleResponse)
    .then((drawings) => drawings as DrawingListItem[]);
};

export const getDrawingDetails = async (
  auth: Credentials,
  drawingId: string,
): Promise<DrawingFromBE> => {
  if (!drawingId) {
    throw new Error('Drawing ID is required');
  }
  const url = `${publicWarningBaseUrl}/drawings/${drawingId}`;

  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<DrawingFromBE>;
};

export const getAreaKeywords = async (auth: Credentials): Promise<string[]> => {
  const url = `${publicWarningBaseUrl}/area-keywords`;

  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<string[]>;
};

export const saveDrawingAs = async (
  auth: Credentials,
  data: DrawingFromFE,
): Promise<Headers> => {
  const response = await fetch(`${publicWarningBaseUrl}/drawings/`, {
    method: 'POST',
    headers: getHeaders(auth),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await throwResponseError(response);
  }

  return response.headers;
};

export const updateDrawing = async (
  auth: Credentials,
  drawingId: string,
  data: DrawingFromFE,
): Promise<void> => {
  const url = `${publicWarningBaseUrl}/drawings/${drawingId}/`;
  return fetch(url, {
    method: 'POST',
    headers: getHeaders(auth),
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<void>;
};

export const deleteDrawing = async (
  auth: Credentials,
  drawingId: string,
): Promise<void> => {
  const url = `${publicWarningBaseUrl}/drawings/${drawingId}?delete=true`;
  return fetch(url, {
    method: 'POST',
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<void>;
};

export const getMaritimeWarningsConfig = async (
  auth: Credentials,
): Promise<MaritimeWarningsConfig> => {
  const url = `${publicWarningBaseUrl}/maritime-warnings-config`;
  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<MaritimeWarningsConfig>;
};

// Warning Events API ----------------------------------------------------------------------------
export const getWarningEventsConfig = async (
  auth: Credentials,
): Promise<WarningEventsConfig> => {
  const url = `${publicWarningBaseUrl}/warning-events-config`;
  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<WarningEventsConfig>;
};

export const getWarningEventList = async (
  auth: Credentials,
  apiParams: Record<string, string> = {},
): Promise<WarningEvent[]> => {
  const params = new URLSearchParams(apiParams);
  const url = `${publicWarningBaseUrl}/events${params.toString() ? `?${params}` : ''}`;
  return fetch(url, {
    headers: getHeaders(auth),
  })
    .then(handleResponse)
    .then((warningEvents: unknown) =>
      (warningEvents as Partial<WarningEvent>[]).map(
        (event: Partial<WarningEvent>) => ({
          ...event,
          type: 'warningsEvent',
        }),
      ),
    ) as Promise<WarningEvent[]>;
};

export const updateWarningEventEditor = async (
  eventId: string,
  isEditor: boolean,
  auth: Credentials,
  force?: boolean,
): Promise<Headers | null> =>
  fetch(
    `${publicWarningBaseUrl}/events/${eventId}?editor=${isEditor ? 'true' : 'false'}&force=${force ? 'true' : 'false'}`,
    {
      headers: getHeaders(auth),
      method: 'POST',
    },
  ).then(handleResponse) as Promise<Headers | null>;

export const updateWarningEvent = async (
  eventId: string,
  auth: Credentials,
  data: WarningEvent,
): Promise<Headers | null> =>
  fetch(`${publicWarningBaseUrl}/events/${eventId}`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers | null>;

export const postWarningEvent = async (
  auth: Credentials,
  data: WarningEvent,
): Promise<Headers | null> =>
  fetch(`${publicWarningBaseUrl}/events/`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers | null>;

export const deleteWarningEvent = async (
  eventId: string,
  auth: Credentials,
): Promise<null> =>
  fetch(`${publicWarningBaseUrl}/events/${eventId}?delete=true`, {
    headers: getHeaders(auth),
    method: 'POST',
  }).then(handleResponse) as Promise<null>;
